import omit from 'lodash.omit';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { startTransition, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  categoryForSlug,
  countryForSlug,
  fetchBrandsInCategoryAndCountry,
  searchBrands,
} from '@/api';
import BrandGrid from '@/components/brand-grid';
import CategoryIcon from '@/components/category-icon';
import FaqList from '@/components/faq-list';
import { FAQSchema } from '@/components/json-ld';
import MaintenanceNotice, { maintenancePageProps } from '@/components/maintenance-notice';
import SearchField from '@/components/search-field';
import SEO from '@/components/seo';
import { mutedStyle, SectionContainer, sectionTitleStyle } from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import { up } from '@/themes';
import { flagForCountryCode, getOtherCountryBrandsFromResults, parseIPAddress } from '@/utils';
import { giftCardPhrase, isAllGiftCards, isGiftCardCategory } from '@/utils/category';
import { hiddenCategoriesForCountry, withCategories } from '@/utils/category-nav';
import { getFallbackCountry } from '@/utils/country';
import { countryNameInSentence } from '@/utils/country-name';
import { shareImage } from '@/utils/share-image';

/* Holds the country select's place until it loads, so the search field does not jump. */
const CountrySelectPlaceholder = styled.span`
  display: block;
  flex-shrink: 0;
  height: 48px;

  @media (min-width: 616px) {
    min-width: max(15em, calc(25% + 1em));
  }
`;

const NewNetworksBanner = dynamic(() => import('@/components/new-networks-banner'), { ssr: false });
const CountrySelect = dynamic(() => import('@/components/country-select'), {
  ssr: false,
  loading: () => <CountrySelectPlaceholder aria-hidden="true" />,
});
const CategorySelect = dynamic(() => import('@/components/category-select'), { ssr: false });
const StarredBrands = dynamic(() => import('@/components/starred-brands'), { ssr: false });

/* The country select and the search field sit side by side from this width. */
const joined = '@media (min-width: 616px)';

/* Search bar: 12px + 48px + 12px. The sidebar sticks just below it. */
const searchBandHeight = 72;

const Page = styled.div`
  padding-bottom: 80px;
`;

const Intro = styled(SectionContainer)`
  padding-top: 24px;

  ${up.sm} {
    padding-top: 32px;
  }
`;

const Title = styled.h1`
  ${sectionTitleStyle};
`;

const IntroCopy = styled.p`
  ${mutedStyle};
  font-size: 16px;
  line-height: 24px;
  margin-top: 8px;
  max-width: 672px;
  text-wrap: pretty;
`;

/*
 * Full-width band, so its frosted background needs no negative offsets (the
 * old header's pseudo-elements overflowed the page). Sticks under the navbar
 * once the country select and search field share a row.
 */
const SearchBand = styled.div`
  padding: 16px 0 12px;
  position: relative;
  z-index: 20;

  ${up.md} {
    -webkit-backdrop-filter: blur(12px);
    backdrop-filter: blur(12px);
    background: color-mix(in srgb, var(--background) 80%, transparent);
    padding: 12px 0;
    position: sticky;
    top: var(--nav-height);

    &::after {
      background: linear-gradient(to bottom, var(--background), transparent);
      content: '';
      height: 16px;
      left: 0;
      pointer-events: none;
      position: absolute;
      right: 0;
      top: 100%;
    }
  }
`;

const outline = css`
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-sm);
`;

const focusOutline = css`
  box-shadow:
    0 0 0 1px var(--ring),
    0 0 0 4px color-mix(in oklab, var(--ring) 25%, transparent);
`;

const SearchBar = styled.div`
  display: flex;
  flex-direction: column;
  /* The select and the search field are sized in em: pin them to 16px. */
  font-size: 16px;
  gap: 12px;

  /* Stacked: two separate pills. */
  && .select__control {
    ${outline};
    border-radius: 9999px !important;
  }

  && .select__control--is-focused {
    ${focusOutline};
  }

  .search-field {
    ${outline};
    background-color: var(--card);
    border-radius: 9999px;
    height: 48px;
    transition: box-shadow 150ms;
    width: 100%;
  }

  .search-field--focused {
    ${focusOutline};
  }

  ${joined} {
    ${outline};
    align-items: center;
    background-color: var(--card);
    border-radius: 9999px;
    flex-direction: row;
    gap: 0;
    height: 48px;
    transition: box-shadow 150ms;

    ${({ $isFocused }) => $isFocused && focusOutline};

    && .select__control,
    && .select__control--is-focused {
      border-bottom-right-radius: 0 !important;
      border-top-right-radius: 0 !important;
      box-shadow: none;
    }

    .search-field,
    .search-field--focused {
      background: none;
      border-radius: 0;
      box-shadow: none;
      width: auto;
    }
  }
`;

const SidebarLayout = styled(SectionContainer)`
  display: flex;
  flex-direction: column;
  padding-top: 4px;

  @media (min-width: 769px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 32px;
    padding-top: 16px;
  }
`;

const CategoriesColumn = styled.aside`
  box-sizing: border-box;

  @media (min-width: 769px) {
    flex-shrink: 0;
    max-height: calc(100vh - var(--nav-height) - ${searchBandHeight + 32}px);
    overflow-y: auto;
    position: sticky;
    top: calc(var(--nav-height) + ${searchBandHeight + 16}px);
    width: 232px;
  }

  .use-custom-scrollbars & {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }
  }

  .use-custom-scrollbars &:hover {
    &::-webkit-scrollbar-thumb {
      background: color-mix(in srgb, var(--foreground) 15%, transparent);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: color-mix(in srgb, var(--foreground) 30%, transparent);
    }
  }
`;

const CategoryNav = styled.nav`
  @media (max-width: 768px) {
    display: none;
  }
`;

const CategoryGroup = styled.p`
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  line-height: 16px;
  margin: 0 0 8px 12px;
  text-transform: uppercase;
`;

const Categories = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
  list-style: none;
  margin: 0 0 24px;
  padding: 0;
`;

const Category = styled(Link)`
  align-items: center;
  border-radius: 9999px;
  color: var(--muted-foreground);
  display: flex;
  font-size: 15px;
  gap: 10px;
  line-height: 20px;
  padding: 8px 12px;
  text-decoration: none;
  transition:
    background-color 150ms,
    color 150ms;

  &:hover {
    background-color: var(--accent);
    color: var(--accent-foreground);
  }

  &:focus-visible {
    outline-offset: -2px;
  }

  &[aria-current='page'] {
    background-color: color-mix(in oklab, var(--primary) 10%, transparent);
    color: var(--primary);
    font-weight: 500;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

/* StarredBrands renders its own h2; size it like the category heading. */
const Favorites = styled.div`
  h2 {
    font-size: 18px;
    letter-spacing: normal;
    line-height: 28px;
    margin: 0 0 8px;
  }
`;

const CategoryTitle = styled.h2`
  color: var(--foreground);
  font-size: 18px;
  font-weight: 600;
  letter-spacing: normal;
  line-height: 28px;
  margin: 0 0 16px;
`;

const FAQSection = styled.section`
  border-top: 1px solid var(--border);
  margin-top: 48px;
  max-width: 768px;
  padding-top: 32px;
`;

const FAQTitle = styled.h2`
  color: var(--foreground);
  font-size: 24px;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 32px;
  margin: 0 0 16px;
`;

/* FaqList centres itself; here it lines up with the grid. */
const Faqs = styled(FaqList)`
  margin-left: 0;
`;

const OptionSeparator = styled.div`
  border-bottom: 1px solid var(--border);
  margin-bottom: 4px;
`;

const formatGroupLabel = (data) => {
  if (data.label === 'Featured') {
    return null;
  }

  return <OptionSeparator />;
};

/** Product types the search API accepts in place of a category. */
const knownProductTypes = [
  'giftcard',
  'topup',
  'data',
  'pin',
  'bundle',
  'digitaltv',
  'esim',
  'longdistance',
];

/** Plain left click: anything else (new tab, new window) is left to the browser. */
const isPlainClick = (event) =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

const Index = ({
  brands,
  category,
  canonicalPath,
  canonicalUrl,
  countries,
  country,
  hiddenCategories,
  otherBrands,
  searchResults,
  searchTerm,
  unknownRoute,
}) => {
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchTerm || '');
  const [selectedCountry, setSelectedCountry] = useState(country);
  const [selectedCategory, setSelectedCategory] = useState(category ? category.slug : 'giftcards');
  const [clientSearchResults, setClientSearchResults] = useState(searchResults);
  const [clientBrands, setClientBrands] = useState(brands);
  const [clientOtherBrands, setClientOtherBrands] = useState(otherBrands);
  const router = useRouter();
  const cT = useTranslations('categories');
  const t = useTranslations('buy');
  const setFocus = (value) => () => setIsFocused(value);
  const featuredCountries = ['US', 'CA', 'GB', 'IT', 'FR', 'DE', 'AE'];
  const isSearching = searchQuery.trim().length >= 3;
  const shouldShowFavorites =
    !isSearching && (!selectedCategory || selectedCategory === 'giftcard');

  const formattedCountries = countries.map((country) => ({
    value: country.id,
    label: `${country.id === 'XG' ? '🌎' : flagForCountryCode(country.id)} ${country.name}`,
    ...country,
  }));

  const countrySelectOptions = [
    {
      label: 'Featured',
      options: formattedCountries.filter((country) => featuredCountries.includes(country.id)),
    },
    {
      label: 'All',
      options: formattedCountries.filter((country) => !featuredCountries.includes(country.id)),
    },
  ];

  let categoryOptions = [];

  const getGroupName = (groupName) => {
    return cT(`groups.${groupName}`);
  };

  // A slug the catalogue returns before it has a translation falls back to its name.
  const labelForSlug = (slug, fallback) => (cT.has(slug) ? cT(slug) : fallback || slug);

  const getCategoryLabel = (category) => {
    return labelForSlug(category.slug || category.name, category.name);
  };

  const getBrandsFromResults = (results, cat) => {
    let newBrands = [];

    if (cat) {
      results.groups.forEach((group) => {
        const matchingCategory = group.categories.filter((c) => c.name === cat.name)[0];

        if (matchingCategory) {
          newBrands = matchingCategory.brands;
        }
      });
    } else {
      const firstCategory = results?.groups?.[0]?.categories?.[0]?.name;

      if (firstCategory) {
        newBrands = results.groups[0].categories[0].brands;
        delete results.groups[0].categories[0].brands;
        cat = results.groups[0].categories[0];
        if (!cat.slug) {
          cat.slug = cat.name;
        }
      }
    }

    return newBrands;
  };

  if (clientSearchResults) {
    // The search groups never list a hidden category (Health & Spa): add the ones
    // this country has brands in. Search results show only what the search found.
    const groups = isSearching
      ? clientSearchResults.groups
      : withCategories(clientSearchResults.groups, hiddenCategories);

    categoryOptions = groups.map((group) => ({
      label: getGroupName(group.name),
      options: group.categories.map((category) => ({
        label: getCategoryLabel(category),
        value: category.slug || category.name,
      })),
    }));
  }

  const onChangeCountry = (country) => {
    setSelectedCountry(country);
    setSelectedCategory(null);
    setLoading(true);
    setSearchQuery('');

    return router.push(`/buy/${country.slug}`);
  };

  const selectCategory = (categorySlug) => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get('q');
    setSelectedCategory(categorySlug);
    setLoading(true);

    if (category) {
      let url = `/buy/${country.slug}/${categorySlug}`;

      if (query) {
        url = `${url}?q=${query}`;
      }

      return router.push(url);
    }

    return router.push(`/buy/${country.slug}`);
  };

  const onCategoryClick = (categorySlug) => (event) => {
    if (!isPlainClick(event)) return;

    event.preventDefault();
    selectCategory(categorySlug);
  };

  const selectedCountryOption = {
    value: selectedCountry.id,
    label: `${selectedCountry.id === 'XG' ? '🌎' : flagForCountryCode(selectedCountry.id)} ${selectedCountry.name}`,
    ...selectedCountry,
  };

  const setSearchTerm = (e) => {
    setSearchQuery(e.target.value);
  };

  const search = async (event) => {
    setLoading(true);

    const { value } = event.target;
    const { results, otherResults = [] } = await searchBrands({
      countryId: country.id,
      $search: value || undefined,
    });

    // Extract the active category's brands first, then keep only the metadata the
    // sidebar needs, matching the shape getServerSideProps sends.
    const activeBrands = getBrandsFromResults(results);
    results.groups = results.groups.map((group) => ({
      ...group,
      categories: group.categories.map(({ brands: _brands, ...cat }) => cat),
    }));

    setClientBrands(activeBrands);
    setClientSearchResults(results);
    setClientOtherBrands(getOtherCountryBrandsFromResults(otherResults, country.id));
    setSelectedCategory(null);

    if (value.length) {
      window.history.pushState(null, null, `?q=${encodeURIComponent(value)}`);
    } else {
      window.history.pushState(null, null, `/buy/${country.slug}`);
    }

    if (results.groups.length) {
      setSelectedCategory(results.groups[0].categories[0].slug);
    }
  };

  const handleOnReset = () => {
    setLoading(true);
    setSearchQuery('');
    return router.push(`/buy/${country.slug}`);
  };

  // New server props (navigation, back / forward): bring the local state back in step.
  // biome-ignore lint/correctness/useExhaustiveDependencies: country and category always change together with brands
  useEffect(() => {
    startTransition(() => {
      setLoading(false);
      setClientBrands(brands);
      setClientSearchResults(searchResults);
      setSelectedCountry(country);
      setSelectedCategory(category ? category.slug : 'giftcards');
    });
  }, [brands, searchResults]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We need these deps so it properly sets loading to false
  useEffect(() => {
    startTransition(() => {
      setLoading(false);
    });
  }, [clientBrands, clientSearchResults]);

  /*
   * What this URL is about, for the title, H1, intro and FAQ:
   *   index    /buy (country picked from the visitor's location)
   *   country  /buy/{country}, gift cards
   *   category /buy/{country}/{gift card category}
   *   other    a product type that is not a gift card (top-ups, data, vouchers)
   * Derived from the server-rendered props only, so the head never depends on
   * what the visitor types into the search field.
   */
  const isCountryRoute = Array.isArray(router.query.slug) && router.query.slug.length > 0;
  const categorySlug = category?.slug || category?.name;
  let pageKind = 'index';
  if (isCountryRoute) {
    if (!category || isAllGiftCards(categorySlug)) pageKind = 'country';
    else if (isGiftCardCategory(categorySlug)) pageKind = 'category';
    else pageKind = 'other';
  }

  const inCountry = countryNameInSentence(country, router.locale);
  const brandExamples = brands
    .slice(0, 3)
    .map((b) => b.name)
    .join(', ');

  let seoTitle;
  let seoDescription;
  let heading;
  let intro;

  if (pageKind === 'index') {
    seoTitle = t('seoTitleIndex');
    heading = t('h1Index');
    intro = t('introIndex');
    seoDescription = intro;
  } else if (pageKind === 'country') {
    seoTitle = t('seoTitleCountry', { country: country.name });
    heading = t('h1Country', { country: inCountry });
    intro = brandExamples
      ? t('intro', { country: inCountry, brandExamples })
      : t('introNoBrands', { country: inCountry });
    seoDescription = intro;
  } else {
    const isCategory = pageKind === 'category';
    const values = {
      category: isCategory
        ? giftCardPhrase(cT, categorySlug, router.locale)
        : getCategoryLabel(category),
      country: inCountry,
    };
    seoTitle = t('seoTitleCategory', values);
    heading = t('h1Category', values);
    intro = isCategory ? t('introCategory') : t('introOther');
    seoDescription = isCategory
      ? t('seoDescriptionCategory', values)
      : t('seoDescriptionOther', values);
  }

  // The card of what the URL is about, and what it says: the category of the
  // country, the country (also for product types that are not gift cards), or
  // the whole catalogue (the index, and URLs naming a country or category we
  // do not have).
  let shareImageUrl = shareImage.browse(router.locale);
  let shareImageAlt = t('h1Index');
  if (!unknownRoute && pageKind === 'category') {
    shareImageUrl = shareImage.countryCategory(country.slug, categorySlug, router.locale);
    shareImageAlt = heading;
  } else if (!unknownRoute && pageKind !== 'index') {
    shareImageUrl = shareImage.country(country.slug, router.locale);
    shareImageAlt = t('h1Country', { country: inCountry });
  }

  // A country, or a category of it, with nothing to buy has no value in the index.
  const emptyListing = pageKind !== 'index' && brands.length === 0;

  // Gift card questions only make sense on a page that lists gift cards.
  const showFaq =
    !isSearching && (pageKind === 'country' || pageKind === 'category') && brands.length > 0;
  const faqs = showFaq
    ? [
        {
          question: t('faq.q1', { country: inCountry }),
          answer: t('faq.a1', { country: inCountry }),
        },
        {
          question: t('faq.q2', { country: inCountry }),
          answer: t('faq.a2', { country: inCountry, brandExamples }),
        },
        { question: t('faq.q3'), answer: t('faq.a3') },
        { question: t('faq.q4'), answer: t('faq.a4') },
      ]
    : [];

  return (
    <Page>
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={canonicalUrl}
        hreflangPath={canonicalPath}
        imageUrl={shareImageUrl}
        imageAlt={shareImageAlt}
        noindex={Boolean(searchTerm) || isSearching || unknownRoute || emptyListing}
      />
      {/* Same array as the visible list below, so the two cannot drift. */}
      {showFaq && <FAQSchema faqs={faqs} />}
      <NewNetworksBanner />
      <Intro>
        <Title>{heading}</Title>
        {!isSearching && <IntroCopy>{intro}</IntroCopy>}
      </Intro>
      <SearchBand>
        <SectionContainer>
          <SearchBar $isFocused={isFocused}>
            {countrySelectOptions.length > 0 && (
              <CountrySelect
                aria-label={`${t('selectCountry')}`}
                isSearchable
                instanceId="country-select"
                options={countrySelectOptions}
                value={selectedCountryOption}
                onChange={onChangeCountry}
                placeholder={`${t('selectCountry')}`}
                formatGroupLabel={formatGroupLabel}
              />
            )}
            <SearchField
              onBlur={setFocus(false)}
              onFocus={setFocus(true)}
              search={search}
              searchTerm={searchQuery}
              setSearchTerm={setSearchTerm}
            />
          </SearchBar>
        </SectionContainer>
      </SearchBand>
      <SidebarLayout>
        {!!categoryOptions.length && (
          <CategoriesColumn>
            {selectedCountry && clientSearchResults && (
              <CategorySelect
                aria-label={`${t('selectCategory')}`}
                instanceId="category-select"
                isSearchable={false}
                options={categoryOptions}
                value={
                  selectedCategory
                    ? { value: selectedCategory, label: labelForSlug(selectedCategory) }
                    : null
                }
                onChange={(option) => selectCategory(option.value)}
                placeholder={t('selectCategory')}
              />
            )}
            <CategoryNav aria-label={cT('title')}>
              {categoryOptions.map((group) => (
                <div key={group.label}>
                  <CategoryGroup>{group.label}</CategoryGroup>
                  <Categories>
                    {group.options.map(({ label, value }) => (
                      <li key={value}>
                        <Category
                          aria-current={value === selectedCategory ? 'page' : undefined}
                          href={`/buy/${country.slug}/${value}`}
                          onClick={onCategoryClick(value)}
                          prefetch={false}
                        >
                          <CategoryIcon slug={value} size={18} />
                          {label}
                        </Category>
                      </li>
                    ))}
                  </Categories>
                </div>
              ))}
            </CategoryNav>
          </CategoriesColumn>
        )}
        <MainContent>
          {shouldShowFavorites && (
            <Favorites>
              <StarredBrands />
            </Favorites>
          )}
          <CategoryTitle>
            {isSearching
              ? t('searchResults')
              : category
                ? getCategoryLabel(category)
                : cT('giftcard')}
          </CategoryTitle>
          <BrandGrid
            brands={clientBrands}
            country={selectedCountry}
            loading={loading}
            onReset={handleOnReset}
            otherBrands={clientOtherBrands}
            priorityCount={2}
            showEmptyState
            showOtherBrands={
              Array.isArray(clientOtherBrands) && !!clientOtherBrands.length && !!searchQuery.trim()
            }
          />
          {showFaq && (
            <FAQSection id="buy-faq">
              <FAQTitle>{t('faq.title')}</FAQTitle>
              <Faqs faqs={faqs} />
            </FAQSection>
          )}
        </MainContent>
      </SidebarLayout>
    </Page>
  );
};

export async function getServerSideProps({ params, query, req, locale }) {
  // Only /buy, /buy/{country} and /buy/{country}/{category} exist.
  if (params.slug?.length > 2) {
    return { notFound: true };
  }

  if (config.inMaintenanceMode) {
    return maintenancePageProps(locale);
  }

  // console.log('getServerSideProps', 'params', params, 'query', query);
  //TODO: Error handling and log to Bugsnag
  const ipAddress = query.ip || parseIPAddress(req);
  const searchTerm = query.q || null;
  // TODO: Add geo lookup and redirect if base route
  let country;
  let category = null;
  let type;
  let unknownRoute = false;
  if (params.slug) {
    const countrySlug = params.slug[0];
    const categorySlug = params.slug[1] || null;
    country = await countryForSlug(countrySlug);
    if (categorySlug) {
      category = await categoryForSlug(categorySlug);
    }
    if (!category) {
      type = categorySlug || null;
      if (categorySlug) {
        category = {
          slug: categorySlug || null,
          name: categorySlug || null,
        };
        // Neither a category nor a product type we know: render, but keep it out of the index.
        unknownRoute = !knownProductTypes.includes(categorySlug);
      }
    }
    if (!country) {
      unknownRoute = true;
      country = await getFallbackCountry(ipAddress, req);
    }
  } else {
    country = await getFallbackCountry(ipAddress, req);
  }

  const { results, otherResults } = await searchBrands({
    countryId: country.id,
    categoryId: category ? category.id : undefined,
    $search: searchTerm || undefined,
    type,
  });

  const omittedFields = [
    'description',
    'redemptionInstructions',
    'redemptionInstructionsUrl',
    'disclaimer',
    'categories',
    'products',
    'websiteUrl',
    'importantNotes',
    'providerId',
    // 'featured',
    'enabled',
    'discontinued',
    'thirdPartyDisabled',
    'cashbackPercent',
    'redemptionMethods',
    'shortName',
    'country.availableInWallet',
    'country.shortName',
  ];

  results.groups = results.groups.map((group) => {
    group.categories = group.categories.map((cat) => {
      cat.brands = Object.keys(cat.brands).map((key) => omit(cat.brands[key], omittedFields));
      cat.brands.forEach((b) => {
        b.country = omit(b.country, ['availableInWallet', 'shortName']);
      });
      // The list only needs a label and a link: the catalogue's legacy titles
      // and paths stay out of the page data.
      const updatedCat = { name: cat.name, brands: cat.brands };
      if (cat.id) updatedCat.id = cat.id;
      if (cat.slug) updatedCat.slug = cat.slug;
      return updatedCat;
    });
    return group;
  });
  let brands = [];
  let viaHiddenCategory = false;
  if (category) {
    results.groups.forEach((group) => {
      const matchingCategory = group.categories.filter((c) => c.name === category.name)[0];
      if (matchingCategory) {
        brands = matchingCategory.brands.sort((a, b) => {
          // featured: true goes first, then non-featured
          if (a.featured === b.featured) return 0;
          if (a.featured) return -1;
          return 1;
        });
      }
    });

    // Hidden categories (e.g. Health & Spa) are omitted from /search groups.
    if (!brands.length && category.id) {
      const categoryBrands = await fetchBrandsInCategoryAndCountry({
        countryId: country.id,
        categoryId: category.id,
        $search: searchTerm || undefined,
      });
      brands = categoryBrands
        .map((brand) => {
          const trimmed = omit(brand, omittedFields);
          trimmed.country = omit(trimmed.country, ['availableInWallet', 'shortName']);
          return trimmed;
        })
        .sort((a, b) => {
          if (a.featured === b.featured) return 0;
          if (a.featured) return -1;
          return 1;
        });
      viaHiddenCategory = brands.length > 0;
    }
  } else {
    const firstCategory = results?.groups?.[0]?.categories?.[0]?.name;
    if (firstCategory) {
      brands = results.groups[0].categories[0].brands.sort((a, b) => {
        // featured: true goes first, then non-featured
        if (a.featured === b.featured) return 0;
        if (a.featured) return -1;
        return 1;
      });
      delete results.groups[0].categories[0].brands;
      category = results.groups[0].categories[0];
      if (!category.slug) {
        category.slug = category.name;
      }
    }
  }

  // Only the selected category's brands are rendered, and they already ship as the
  // top-level `brands` prop. The category tree is just sidebar labels, so drop every
  // brand list from it instead of serializing the whole catalogue (and the selected
  // category twice).
  results.groups = results.groups.map((group) => ({
    ...group,
    categories: group.categories.map((cat) => omit(cat, 'brands')),
  }));

  const otherBrands = getOtherCountryBrandsFromResults(otherResults, country.id).map((brand) =>
    omit(brand, omittedFields)
  );

  // The hidden categories this country has brands in, for the category list. The
  // API does not say, so this comes from the build data (written by format-data.js).
  const hiddenCategories = hiddenCategoriesForCountry({
    categories: require('@data/cats.json'),
    countryId: country.id,
    hiddenCategoryCountries: require('@data/hidden-category-countries.json'),
  });
  // Brands added since the build: the category being viewed is listed all the same.
  if (viaHiddenCategory && !hiddenCategories.some(({ slug }) => slug === category.slug)) {
    hiddenCategories.push({ id: category.id, name: category.name, slug: category.slug });
  }

  // Canonical: never the search query, and "all gift cards" is the country page itself.
  const pathSegments = params.slug?.length ? [...params.slug] : [];
  if (pathSegments.length > 1 && isAllGiftCards(pathSegments[1])) {
    pathSegments.length = 1;
  }
  const canonicalPath = pathSegments.length ? `/buy/${pathSegments.join('/')}` : '/buy';
  const canonicalUrl =
    locale === i18n.defaultLocale
      ? `${config.rootUrl}${canonicalPath}`
      : `${config.rootUrl}/${locale}${canonicalPath}`;

  return {
    props: {
      searchTerm,
      country,
      // Only what the page reads: the catalogue's legacy titles and paths stay out.
      category: category && {
        ...(category.id ? { id: category.id } : null),
        name: category.name,
        slug: category.slug,
      },
      brands,
      hiddenCategories,
      otherBrands,
      searchResults: results,
      canonicalPath,
      canonicalUrl,
      unknownRoute,
      messages: loadMessages(locale),
    },
  };
}

const BuyPage = (props) => (props.maintenance ? <MaintenanceNotice /> : <Index {...props} />);

export default BuyPage;
